import {
  AlertCircle,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
// import ProviderBadge from '../../components/ui/ProviderBadge';
import { useToast } from '../../contexts/ToastContext';
import {
  deleteDomainRequest,
  getDomainRequest,
  verifyDomainRequest,
  clearDomainState,
} from '../../store/domain/domain.action';
import {
  selectDomainsError,
  selectDomainsLoading,
  selectDomainVerificationLoading,
  selectSelectedDomain,
} from '../../store/domain/domain.selector';
import { RootState } from '../../store/rootReducer';
import { IDnsRecords } from '../../types/dns/dns.types';
import { selectCurrentUser } from 'store/user';

type TabType = 'authentication' | 'link-tracking' | 'mail-servers';

interface HiddenRecords {
  mx: boolean;
  spf: boolean;
  dkim: boolean;
  // Mailgun specific record types
  sending: boolean;
  tracking: boolean;
  authentication: boolean;
}

interface GroupedRecords {
  dkim?: IDnsRecords[];
  spf?: IDnsRecords[];
  mx?: IDnsRecords[];
  dmarc?: IDnsRecords[];
  // Mailgun specific groupings
  sending?: IDnsRecords[];
  tracking?: IDnsRecords[];
  authentication?: IDnsRecords[];
}

interface RecordTableProps {
  title: string;
  records: IDnsRecords[];
  recordType: string;
}

const DomainRecords: React.FC = () => {
  const user = useSelector(selectCurrentUser);
  const { domainId } = useParams<{ domainId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const domain = useSelector((state: RootState) => selectSelectedDomain(state));
  const loading = useSelector((state: RootState) =>
    selectDomainsLoading(state)
  );
  const error = useSelector((state: RootState) => selectDomainsError(state));
  const verificationLoading = useSelector((state: RootState) =>
    selectDomainVerificationLoading(state)
  );

  const [activeTab, setActiveTab] = useState<TabType>('authentication');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [hiddenRecords, setHiddenRecords] = useState<HiddenRecords>({
    mx: false,
    spf: false,
    dkim: false,
    sending: false,
    tracking: false,
    authentication: false,
  });

  useEffect(() => {
    if (domainId) {
      dispatch(getDomainRequest(domainId) as any);
    }
  }, [domainId, dispatch]);

  // Show toast notification when there's an error
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
      dispatch(clearDomainState());
    }
  }, [error, showToast, dispatch]);

  const copyToClipboard = async (
    text: string,
    field: string,
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.blur();

    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const toggleRecordVisibility = (record: keyof HiddenRecords): void => {
    setHiddenRecords((prev) => ({
      ...prev,
      [record]: !prev[record],
    }));
  };

  const groupRecordsByType = (
    records: IDnsRecords[],
    domainType?: string
  ): GroupedRecords => {
    if (domainType === 'mailgun') {
      return records.reduce((acc: GroupedRecords, record: IDnsRecords) => {
        if (record.type === 'TXT') {
          if (record.value.includes('v=DMARC1')) {
            acc.authentication = [...(acc.authentication || []), record];
          } else if (
            record.value.includes('v=spf1') ||
            record.name.includes('_domainkey')
          ) {
            acc.sending = [...(acc.sending || []), record];
          } else {
            acc.sending = [...(acc.sending || []), record];
          }
        } else if (record.type === 'CNAME') {
          acc.tracking = [...(acc.tracking || []), record];
        }
        return acc;
      }, {});
    } else {
      return records.reduce((acc: GroupedRecords, record: IDnsRecords) => {
        if (record.type === 'TXT' && record.name.includes('_domainkey')) {
          acc.dkim = [...(acc.dkim || []), record];
        } else if (record.type === 'TXT' && record.value.includes('v=spf1')) {
          acc.spf = [...(acc.spf || []), record];
        } else if (record.type === 'MX') {
          acc.mx = [...(acc.mx || []), record];
        } else if (record.type === 'TXT' && record.value.includes('v=DMARC1')) {
          acc.dmarc = [...(acc.dmarc || []), record];
        }
        return acc;
      }, {});
    }
  };

  const RecordTable: React.FC<RecordTableProps> = ({
    title,
    records,
    recordType,
  }) => (
    <div className="mb-8 bg-card rounded-xl border border-border p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <Button
          variant="ghost"
          size="small"
          onClick={() =>
            toggleRecordVisibility(recordType as keyof HiddenRecords)
          }
          className="text-primary hover:text-primary-hover"
        >
          {hiddenRecords[recordType as keyof HiddenRecords] ? (
            <>
              <Eye className="w-4 h-4 mr-1.5" />
              Show records
            </>
          ) : (
            <>
              <EyeOff className="w-4 h-4 mr-1.5" />
              Hide records
            </>
          )}
        </Button>
      </div>

      {!hiddenRecords[recordType as keyof HiddenRecords] && (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            {recordType === 'dkim'
              ? 'Enable email signing and specify authorized senders.'
              : recordType === 'dmarc'
                ? 'Set authentication policies and receive reports.'
                : recordType === 'sending'
                  ? 'Configure sending authentication for your domain.'
                  : recordType === 'tracking'
                    ? 'Configure link tracking and analytics for your emails. (Note: For tracking records, you must disable proxy status on your DNS provider.)'
                    : recordType === 'authentication'
                      ? 'Set up domain authentication policies.'
                      : 'Add DNS records that match the following:'}
          </p>

          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[10%]" />
                <col className="w-[12%]" />
                <col className="w-[18%]" />
                <col className="w-[40%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                    Content
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                    TTL
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                    Priority
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      No {recordType} records found
                    </td>
                  </tr>
                ) : (
                  records.map((record, index) => (
                    <tr
                      key={index}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {record.status === 'verified' ? (
                          <CheckCircle2 className="w-5 h-5 text-success inline" />
                        ) : record.status === 'pending' ? (
                          <Clock className="w-5 h-5 text-warning inline" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-muted-foreground inline" />
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div
                          className="text-sm text-foreground font-mono truncate"
                          title={record.type}
                        >
                          {record.type}
                        </div>
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="truncate text-sm text-foreground font-mono min-w-0"
                            title={record.name}
                          >
                            {record.name}
                          </span>
                          <Button
                            variant="ghost"
                            size="small"
                            type="button"
                            onClick={(e) =>
                              copyToClipboard(
                                record.name,
                                `name-${recordType}-${index}`,
                                e
                              )
                            }
                            className="shrink-0"
                            aria-label={
                              copiedField === `name-${recordType}-${index}`
                                ? 'Copied'
                                : 'Copy name'
                            }
                          >
                            {copiedField === `name-${recordType}-${index}` ? (
                              <Check className="w-4 h-4 text-success" />
                            ) : (
                              <Copy className="w-4 h-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="truncate text-sm text-foreground font-mono min-w-0"
                            title={
                              record.type === 'MX'
                                ? domain?.type === 'mailgun'
                                  ? record.value.replace(/^\d+/, '')
                                  : record.value.split(' ').slice(1).join(' ')
                                : record.value
                            }
                          >
                            {record.type === 'MX'
                              ? domain?.type === 'mailgun'
                                ? record.value.replace(/^\d+/, '') // Remove leading digits for Mailgun
                                : record.value.split(' ').slice(1).join(' ') // Split by space for AWS
                              : record.value}
                          </span>
                          <Button
                            variant="ghost"
                            size="small"
                            type="button"
                            onClick={(e) =>
                              copyToClipboard(
                                record.type === 'MX'
                                  ? domain?.type === 'mailgun'
                                    ? record.value.replace(/^\d+/, '') // Remove leading digits for Mailgun
                                    : record.value.split(' ').slice(1).join(' ') // Split by space for AWS
                                  : record.value,
                                `${recordType}-${index}`,
                                e
                              )
                            }
                            className="shrink-0"
                            aria-label={
                              copiedField === `${recordType}-${index}`
                                ? 'Copied'
                                : 'Copy content'
                            }
                          >
                            {copiedField === `${recordType}-${index}` ? (
                              <Check className="w-4 h-4 text-success" />
                            ) : (
                              <Copy className="w-4 h-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div
                          className="text-sm text-foreground font-mono truncate"
                          title={String((record as any).ttl || 'Auto')}
                        >
                          {(record as any).ttl || 'Auto'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-foreground font-mono">
                          {record.type === 'MX'
                            ? domain?.type === 'mailgun'
                              ? record.value.match(/^\d+/)?.[0] || '-' // Extract leading digits for Mailgun
                              : record.value.split(' ')[0] // Split by space for AWS
                            : (record as any).priority || '-'}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  const renderTabContent = (): React.ReactElement | null => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mb-4" />
          <p className="text-muted-foreground text-sm">
            Loading domain records…
          </p>
        </div>
      );
    }

    if (!domain) {
      return (
        <div className="text-center py-16">
          <p className="text-muted-foreground">No domain data found.</p>
        </div>
      );
    }

    const groupedRecords: GroupedRecords = groupRecordsByType(
      domain.dnsRecords || [],
      domain.type
    );

    switch (activeTab) {
      case 'authentication':
        return (
          <div className="rounded-lg w-full">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-1">
                  Domain authentication
                </h2>
                <p className="text-sm text-muted-foreground">
                  Authenticate each From Address domain to allow sending email
                  messages.
                </p>
              </div>
            </div>

            {domain.type === 'mailgun' ? (
              // Mailgun specific record display
              <>
                {/* Sending Records */}
                <RecordTable
                  title="Sending Records"
                  records={groupedRecords.sending || []}
                  recordType="sending"
                />

                {/* Tracking Records */}
                <RecordTable
                  title="Tracking Records"
                  records={groupedRecords.tracking || []}
                  recordType="tracking"
                />

                {/* Authentication Records */}
                <RecordTable
                  title="Authentication Records"
                  records={groupedRecords.authentication || []}
                  recordType="authentication"
                />
              </>
            ) : (
              // AWS specific record display (current logic)
              <>
                {/* Display DKIM, SPF and MX records together */}
                {(groupedRecords.dkim ||
                  groupedRecords.spf ||
                  groupedRecords.mx) && (
                  <RecordTable
                    title="Email Authentication Records"
                    records={[
                      ...(groupedRecords.dkim || []),
                      ...(groupedRecords.spf || []),
                      ...(groupedRecords.mx || []),
                    ]}
                    recordType="dkim"
                  />
                )}

                {/* Then display DMARC */}
                {groupedRecords.dmarc && (
                  <RecordTable
                    title="DMARC"
                    records={groupedRecords.dmarc}
                    recordType="dmarc"
                  />
                )}
              </>
            )}
          </div>
        );

      case 'link-tracking':
        return (
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-1">
              Link tracking
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Configure link tracking settings for your domain to track clicks
              and engagement.
            </p>
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                Link tracking configuration options will be displayed here.
              </p>
            </div>
          </div>
        );

      case 'mail-servers':
        return (
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-1">
              Mail servers
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Configure mail server settings and IP pools for your domain.
            </p>
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                Mail server configuration options will be displayed here.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleDelete = (): void => {
    if (domain?._id) {
      const provider = domain.type || 'aws'; // Default to AWS if not specified
      dispatch(deleteDomainRequest(domain._id, provider) as any);
    }
    setIsDeleteModalOpen(false);
    navigate('/domain-setup');
  };

  return (
    <div className="p-6 space-y-6 bg-background">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-foreground">
              DNS records
            </h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Add these records at your DNS provider to verify and send from this
            domain
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="danger"
            size="small"
            disabled={loading || domain?.user_id !== user?._id}
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Delete domain
          </Button>
          <Button
            variant="outline"
            size="small"
            onClick={() => navigate('/domain-setup')}
          >
            Close
          </Button>
        </div>
      </div>

      {/* Domain header */}
      <div className="bg-card rounded-xl border border-border p-6 shadow-sm mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <span
                className={`w-3 h-3 rounded-full shrink-0 ${
                  domain?.status === 'verified'
                    ? 'bg-success'
                    : 'bg-destructive'
                }`}
                aria-hidden
              />
              <h2 className="text-lg font-semibold text-foreground">
                {domain?.domain}
              </h2>
            </div>
            <span
              className={`text-sm font-medium ${
                domain?.status === 'verified'
                  ? 'text-success'
                  : 'text-destructive'
              }`}
            >
              {domain?.status === 'verified' ? 'Verified' : 'Unverified'}
            </span>
            {/* {domain?.type && (
              <ProviderBadge
                provider={domain.type}
                size="medium"
                variant="default"
                showTooltip={true}
              />
            )} */}
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              if (domain?.domain) {
                const provider = domain.type || 'aws';
                dispatch(
                  verifyDomainRequest(
                    domain._id || '',
                    'domainRecords',
                    provider
                  ) as any
                );
              }
            }}
            disabled={verificationLoading}
          >
            <RefreshCw
              className={`w-4 h-4 mr-1.5 ${verificationLoading ? 'animate-spin' : ''}`}
            />
            {verificationLoading ? 'Verifying…' : 'Verify domain'}
          </Button>
        </div>
      </div>

      {/* Tabs – same pill style as SegmentDetails, ContactsDetails, CampaignsV2 */}
      <div className="w-full mb-6">
        <div className="inline-flex items-center gap-1 rounded-lg bg-muted/80 p-2 border border-border/50">
          {(
            [
              { id: 'authentication' as TabType, label: 'Authentication' },
              { id: 'link-tracking' as TabType, label: 'Link tracking' },
              { id: 'mail-servers' as TabType, label: 'Mail servers' },
            ] as const
          ).map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex justify-center items-center gap-2 px-3 py-2 rounded-md text-sm font-medium w-full whitespace-nowrap ${
                activeTab === id
                  ? 'bg-primary/80 text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div>{renderTabContent()}</div>

      {/* Delete modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete domain"
      >
        <p className="text-muted-foreground mb-6">
          Are you sure you want to delete this domain? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DomainRecords;
